import React from 'react';
import { ConventionMetadata } from '../../types';
import { format, parseISO } from 'date-fns';
import { safeFormatISO } from '../../lib/utils';
import { Calendar, MapPin, Users, Settings, ExternalLink, Share2, Copy } from 'lucide-react';
import { toast } from 'sonner';

interface ConListProps {
  conventions: ConventionMetadata[];
  onSelect: (id: string) => void;
  currentUserUid: string;
  isPublicMode?: boolean;
}

export default function ConList({ conventions, onSelect, currentUserUid, isPublicMode = false }: ConListProps) {
  if (conventions.length === 0) {
    return (
      <div className="py-24 text-center bg-white/5 border-2 border-dashed border-white/10 rounded-[40px]">
        <Calendar className="w-16 h-16 mx-auto mb-4 text-[var(--foreground)]/10" />
        <p className="text-xl font-bold text-[var(--foreground)]/40 uppercase tracking-widest">No active operations detected.</p>
        <p className="text-sm text-[var(--foreground)]/20 uppercase tracking-widest mt-2">Create your first convention to begin planning.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {conventions.map(con => (
        <div key={con.id} className="bg-[var(--background)] border-l-8 border-lcars-blue flex flex-col group hover:bg-white/5 transition-all">
          <div className="bg-lcars-blue text-black px-4 py-1 flex justify-between items-center font-bold text-[0.7rem] tracking-widest uppercase">
            <span>{con.status} operation</span>
            <span>{con.ownerId === currentUserUid ? 'ORIGINATOR' : 'EXTERNAL'}</span>
          </div>
          
          <div className="p-6 flex-grow space-y-4">
            <a 
              href={`#/signup/${con.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-2xl font-bold text-[var(--foreground)] hover:text-lcars-blue transition-colors line-clamp-1 decoration-transparent hover:underline"
            >
              {con.name}
            </a>
            
            <div className="space-y-3 text-[0.85rem] font-bold">
              <div className="flex items-center gap-3 text-lcars-purple uppercase">
                <Calendar className="w-4 h-4" />
                <span>{con.startDate && con.endDate ? `${safeFormatISO(con.startDate, 'MMM dd')} - ${safeFormatISO(con.endDate, 'MMM dd, yyyy')}` : 'Dates TBD'}</span>
              </div>
              <div className="flex items-center gap-3 text-lcars-tan">
                <MapPin className="w-4 h-4" />
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(con.address || '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="truncate hover:underline"
                >
                  {con.address || 'NO COORDINATES SPECIFIED'}
                </a>
              </div>
            </div>
          </div>

          <div className="p-6 pt-0 mt-auto flex flex-col gap-2">
            {!isPublicMode ? (
              <>
                <button 
                  onClick={() => onSelect(con.id!)}
                  className="w-full h-12 lcars-pill bg-lcars-blue text-black flex items-center justify-center gap-2 font-bold uppercase transition-all mb-2"
                >
                  <Settings className="w-5 h-5" /> OPEN CONVENTION
                </button>
                <div className="flex gap-2">
                  <a 
                    href={`${window.location.origin}${window.location.pathname}#/schedule/${con.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 h-10 lcars-pill bg-lcars-blue text-black flex items-center justify-center gap-2 font-bold uppercase text-[0.6rem] hover:opacity-80 transition-all"
                  >
                    <Calendar className="w-3 h-3" /> PUBLIC SCHEDULE
                  </a>
                  <button 
                    onClick={() => {
                      const url = `${window.location.origin}${window.location.pathname}#/signup/${con.id}`;
                      navigator.clipboard.writeText(url);
                      toast.success('Link copied to clipboard');
                    }}
                    className="flex-1 h-10 lcars-pill bg-lcars-orange text-black flex items-center justify-center gap-2 font-bold uppercase text-[0.6rem] hover:opacity-80 transition-all"
                  >
                    <Copy className="w-3 h-3" /> COPY SIGNUP LINK
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-col gap-2 w-full">
                <a 
                  href={`#/signup/${con.id}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-full h-12 lcars-pill bg-lcars-tan text-black flex items-center justify-center gap-2 font-bold uppercase transition-all hover:opacity-80"
                >
                  <ExternalLink className="w-5 h-5" /> SIGN UP FOR SHIFT
                </a>
                <div className="flex gap-2">
                  <a 
                    href={`${window.location.origin}${window.location.pathname}#/schedule/${con.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 h-10 lcars-pill bg-lcars-blue text-black flex items-center justify-center gap-2 font-bold uppercase text-[0.6rem] hover:opacity-80 transition-all"
                  >
                    <Calendar className="w-3 h-3" /> PUBLIC SCHEDULE
                  </a>
                  <button 
                    onClick={() => {
                      const url = `${window.location.origin}${window.location.pathname}#/signup/${con.id}`;
                      navigator.clipboard.writeText(url);
                      toast.success('Link copied to clipboard');
                    }}
                    className="flex-1 h-10 lcars-pill bg-lcars-orange text-black flex items-center justify-center gap-2 font-bold uppercase text-[0.6rem] hover:opacity-80 transition-all"
                  >
                    <Copy className="w-3 h-3" /> COPY SIGNUP LINK
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
